import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, Wifi, WifiOff } from 'lucide-react';
import { TERMINAL_HOME, TERMINAL_KIND_LABELS, type TerminalKind } from '@erp/shared';
import { useAuth } from '@/lib/auth';
import { cn, initials } from '@/lib/utils';
import { useEventStream } from '@/hooks/useEventStream';
import { Button } from '@/components/ui/button';
import { ShellContext } from './shell-context';
import { ImpersonationBanner } from './ImpersonationBanner';
import { stationNavItems } from './navigation';
import { ARM_TIMEOUT_MS, endShiftStep, type EndShiftState } from './end-shift';

/**
 * Armazón de los puestos de fábrica (almacén, embalaje, logística): sin menús desplegables,
 * sin buscador ni selector de idioma o tema, a pantalla completa y con objetivos táctiles.
 *
 * Solo pinta las entradas que el usuario puede usar en este terminal —`stationNavItems`, que
 * se apoya en la misma `visibleNav` que el armazón de escritorio, sin la raíz— y deja a
 * mano lo único que se necesita en un puesto: en qué terminal estás, quién eres, si hay
 * conexión y cómo cerrar el turno.
 */
export function TerminalShell() {
  const { t, i18n } = useTranslation();
  const { user, terminal, session, can, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  const [clock, setClock] = useState(() => new Date());
  /**
   * «Cerrar turno» se confirma en dos tiempos y nunca con `window.confirm`: el lector de
   * códigos escribe y termina con Enter, así que un aviso nativo abierto se acepta con el
   * siguiente escaneo y el operario se queda fuera del puesto a mitad de turno. La regla
   * está en `end-shift.ts`, que sí tiene tests: armar, esperar y confirmar.
   */
  const [endShift, setEndShift] = useState<EndShiftState>({ armedAt: null });
  const confirmEnd = endShift.armedAt !== null;
  const armedRef = useRef<number | null>(null);
  armedRef.current = endShift.armedAt;
  useEventStream(Boolean(session));

  const items = useMemo(() => stationNavItems(can, terminal?.kind), [can, terminal?.kind]);

  useEffect(() => {
    if (endShift.armedAt === null) return;
    const timer = setTimeout(() => {
      armedRef.current = null;
      setArmedBy(null);
      setEndShift({ armedAt: null });
    }, ARM_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [endShift.armedAt]);

  /** Cómo se armó, solo para el texto del aviso (el dedo dice «toca», el teclado «espacio»). */
  const [armedBy, setArmedBy] = useState<'pointer' | 'keyboard' | null>(null);

  const onEndShiftClick = (detail: number) => {
    const step = endShiftStep({ armedAt: armedRef.current }, { detail, now: Date.now() });
    armedRef.current = step.state.armedAt;
    setEndShift(step.state);
    if (step.state.armedAt !== null && armedBy === null) setArmedBy(detail === 0 ? 'keyboard' : 'pointer');
    if (step.action === 'confirm') {
      setArmedBy(null);
      void logout();
    }
  };

  useEffect(() => {
    const timer = setInterval(() => setClock(new Date()), 30_000);
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      clearInterval(timer);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    if (!terminal) return;
    // El inicio del puesto, no la raíz de la aplicación: al cerrar un diálogo o recargar no
    // se debe aterrizar en el panel de oficina.
    if (location.pathname === '/') navigate(TERMINAL_HOME[terminal.kind as TerminalKind] ?? '/', { replace: true });
  }, [location.pathname, navigate, terminal]);

  const kindLabel = terminal
    ? TERMINAL_KIND_LABELS[terminal.kind as keyof typeof TERMINAL_KIND_LABELS] ?? terminal.kind
    : '';

  return (
    <ShellContext.Provider value={{ variant: 'station' }}>
      <div className="flex h-full min-h-0 flex-col bg-background">
        <ImpersonationBanner />
        <header className="z-40 shrink-0 border-b border-border bg-card">
          <div className="flex h-14 items-center gap-3 px-3 lg:px-4">
            <div className="flex min-w-0 shrink-0 items-center gap-2">
              <span
                className={cn('size-2 rounded-full', online ? 'bg-emerald-500' : 'bg-destructive')}
                aria-hidden
              />
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold leading-4 text-foreground">
                  {terminal?.code.replace(/^TERMINAL-/, '') ?? t('station.noTerminal')}
                </p>
                <p className="truncate text-[11px] leading-3 text-muted-foreground">
                  {kindLabel}{terminal?.warehouseName ? ` · ${terminal.warehouseName}` : ''}
                </p>
              </div>
            </div>

            <nav aria-label={t('station.nav')} className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto">
              {items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  aria-label={t(item.labelKey)}
                  className={({ isActive }) => cn(
                    'flex h-12 shrink-0 items-center gap-2 rounded-lg px-4 text-base font-medium transition',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <item.icon className="size-5" aria-hidden />
                  <span className="hidden md:inline">{t(item.labelKey)}</span>
                </NavLink>
              ))}
            </nav>

            <div className="flex shrink-0 items-center gap-2">
              <span
                role="status"
                aria-live="polite"
                className={cn('flex items-center gap-1 text-[11px]', online ? 'text-emerald-600' : 'text-destructive')}
                title={online ? t('station.connected') : t('station.offline')}
              >
                {online ? <Wifi className="size-4" aria-hidden /> : <WifiOff className="size-4" aria-hidden />}
                <span className="sr-only lg:not-sr-only">
                  {online ? t('station.connected') : t('station.offline')}
                </span>
              </span>
              <span className="hidden font-mono text-sm tabular-nums text-muted-foreground sm:inline">
                {clock.toLocaleTimeString(i18n.language === 'en' ? 'en-GB' : 'es-ES', { hour: '2-digit', minute: '2-digit' })}
              </span>
              <span className="flex size-10 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary" title={user?.fullName}>
                <span aria-hidden>{initials(user?.fullName ?? 'EB')}</span>
                <span className="sr-only">{user?.fullName}</span>
              </span>
              <Button
                variant={confirmEnd ? 'destructive' : 'outline'}
                size="touch"
                aria-label={confirmEnd ? t('station.endShiftConfirmKey') : t('station.endShift')}
                // El botón no toma el foco con el dedo: si lo tomara, el Enter con el que el
                // lector de códigos cierra cada lectura lo volvería a pulsar, y dos escaneos
                // seguidos cerrarían el turno sin que nadie toque la pantalla.
                // El lector de códigos termina cada lectura con Enter, así que Enter queda
                // bloqueado y se desarma al perder el foco: con el botón enfocado, dos
                // lecturas seguidas cerrarían el turno sin que nadie toque la pantalla.
                // Un usuario de teclado confirma con **espacio** (WCAG 2.1.1), y el dedo, con
                // dos toques separados al menos medio segundo.
                onMouseDown={(event) => event.preventDefault()}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') event.preventDefault();
                }}
                onBlur={() => {
                  armedRef.current = null;
                  setArmedBy(null);
                  setEndShift({ armedAt: null });
                }}
                onClick={(event) => onEndShiftClick(event.detail)}
              >
                <LogOut className="size-5" aria-hidden />
                {/* El aviso se ve en todos los anchos: en tableta el rojo solo no basta. */}
                <span className={cn(confirmEnd ? 'inline' : 'hidden lg:inline')}>
                  {confirmEnd
                    ? t(armedBy === 'keyboard' ? 'station.endShiftConfirmKey' : 'station.endShiftConfirmTouch')
                    : t('station.endShift')}
                </span>
              </Button>
              {/* Fuera del botón: los hijos de un `button` son presentacionales y el
                  `aria-label` tapa su contenido, así que dentro no se anunciaría nada. */}
              <span role="status" aria-live="polite" className="sr-only">
                {confirmEnd
                  ? t(armedBy === 'keyboard' ? 'station.endShiftConfirmKey' : 'station.endShiftConfirmTouch')
                  : ''}
              </span>
            </div>
          </div>
        </header>

        <main className="min-h-0 w-full flex-1 overflow-auto p-3 lg:p-4">
          <div className="animate-fade-in h-full">
            <Outlet />
          </div>
        </main>
      </div>
    </ShellContext.Provider>
  );
}
